import { useEffect, useState, useRef, useCallback } from 'react';
import RestHelper from "utils/RestHelper";
import useZuStore from "store/zustand/zustand-store";
import { useDispatch, useSelector } from "react-redux";
import { ExceptionCategoryUtil } from "utils/exception-category";
import { getIssueDetailUrlPrefix } from "utils/helper";
import { Base64 } from "utils/base64";
import { createPartialJsonParser } from "utils/partial-json-parser";
import { AiIssueAnalysisUtil } from "./ai-issue-analysis";
const LIKE = 'Helpful';
const DISLIKE = 'UnHelpful';
function useLikeDislike(appId, aiResultId) {
    const [isLike, setIsLike] = useState(false);
    const [isDislike, setIsDislike] = useState(false);
    useEffect(() => {
        if (!appId) {
            return;
        }
        setIsLike(false);
        setIsDislike(false);
        fetchLikeOrDislikeStatus();
    }, [appId, aiResultId]);
    async function fetchLikeOrDislikeStatus() {
        if (!aiResultId) {
            return;
        }
        const rsp = await RestHelper.post('/redir/api/aiResult/getEvaluate', {
            appId,
            id: aiResultId,
        }, undefined);
        const data = rsp.json.data;
        const evaluation = data?.evaluation;
        setIsLike(evaluation === LIKE);
        setIsDislike(evaluation === DISLIKE);
    }
    async function onClickLikeOrDislike(isLikeNotDislike) {
        if (!aiResultId) {
            return;
        }
        await RestHelper.post('/redir/api/aiResult/editAiResult', {
            appId,
            id: aiResultId,
            evaluation: isLikeNotDislike ? LIKE : DISLIKE,
        }, undefined);
        setIsLike(isLikeNotDislike);
        setIsDislike(!isLikeNotDislike);
    }
    return {
        isLike,
        isDislike,
        onClickLikeOrDislike,
    };
}
function useIssueAnalysisQuoteHandler() {
    const currentAppFromJs = useSelector((state) => state.app.get('current'));
    const currentIssueFromJs = useSelector((state) => state.issue.get('current'));
    const aiDrawerSlice = useZuStore().aiDrawer;
    const dispatch = useDispatch();
    function handler(crashId, logCategory, log, matchedLogLine) {
        const currentIssueObj = currentIssueFromJs.toJS() || {};
        const { issueId, issueExceptionType } = currentIssueObj;
        const exceptionCategory = ExceptionCategoryUtil.fromExceptionTypeInt(issueExceptionType);
        const crashMap = currentIssueObj?.crashDoc?.crashMap;
        const currentCrashId = crashMap?.crashId || '';
        if (crashId === currentCrashId) {
            aiDrawerSlice.setQuotedLog(crashId, logCategory, log, matchedLogLine);
            dispatch({
                type: 'CHANGE_TAB',
                value: 'LASTREPORT.trackLog',
                tab: 2,
            });
            dispatch({
                type: 'CHANGE_LEVEL',
                level: 'verbose',
            });
            dispatch({
                type: 'CHANGE_LOG',
                logType: logCategory === 'SYSTEM' ? 'sysLog' : 'userLog',
            });
        }
        else { // 需要打开新页面
            const currentAppObj = currentAppFromJs.toJS();
            const { appId, pid } = currentAppObj;
            const quotedLogInfo = { crashId, logCategory, log, matchedLogLine };
            const quotedLogInfoBase64 = Base64.encodeAsString((JSON.stringify(quotedLogInfo)));
            const relatedCrashUrl = `/${getIssueDetailUrlPrefix(exceptionCategory)}/${appId}/${issueId}/report-id/${crashId}?pid=${pid}&quotedLogInfoBase64=${encodeURIComponent(quotedLogInfoBase64)}`;
            window.open(relatedCrashUrl, '_blank');
        }
    }
    return handler;
}
function useConvertAnswerToMarkdownText(text) {
    const previousRawTextRef = useRef('');
    const previousProcessedTextRef = useRef('');
    const parserRef = useRef(null);
    const [markdownText, setMarkdownText] = useState('');
    const processText = useCallback((inputText) => {
        if (!inputText) {
            setMarkdownText('');
            return;
        }
        // console.log('gomoxx inputText: ', inputText);
        // 去掉开头的```json和结尾的```
        let processedText = inputText.trim()
            .replace(/^```json\n/, '')
            .replace(/```$/, '')
            .trimStart();
        // console.log('gomoxx processedText: ', processedText);
        // 判断是否以左花括号开头，不是的话直接返回作为markdown
        const isJson = processedText.startsWith('{');
        // console.log('gomoxx isJson: ', isJson);
        if (!isJson) {
            setMarkdownText(processedText);
            return;
        }
        // 检查是否是增量更新（基于原始文本判断）
        const previousRawText = previousRawTextRef.current;
        const isIncremental = previousRawText && inputText.startsWith(previousRawText);
        // console.log('gomoxx isIncremental: ', isIncremental);
        if (!isIncremental) {
            // 重新初始化解析器
            parserRef.current = createPartialJsonParser();
            previousRawTextRef.current = '';
            previousProcessedTextRef.current = '';
        }
        // 计算增量部分（基于处理后的文本）
        const previousProcessedText = previousProcessedTextRef.current;
        const incrementalPart = processedText.slice(previousProcessedText.length);
        if (incrementalPart && parserRef.current) {
            // console.log('gomoxx incrementalPart: ', incrementalPart);
            try {
                const result = parserRef.current.parse(incrementalPart);
                if (result.isComplete) {
                    // 完整JSON，直接转换为markdown
                    const completeJson = JSON.stringify(result.data);
                    // console.log('gomoxx complete json: ', completeJson);
                    const markdownResult = AiIssueAnalysisUtil.convertJsonTextToMarkdownText(completeJson);
                    setMarkdownText(markdownResult);
                }
                else {
                    // 部分JSON，转换已解析的部分为markdown
                    const partialJson = JSON.stringify(result.data);
                    // console.log('gomoxx partial json: ', partialJson);
                    const markdownResult = AiIssueAnalysisUtil.convertJsonTextToMarkdownText(partialJson);
                    setMarkdownText(markdownResult);
                }
            }
            catch (e) {
                console.error('useConvertAnswerToMarkdownText parse error: ', e);
                // 解析失败时返回原文本
                setMarkdownText(processedText);
            }
        }
        // 更新上一次的原始文本和处理后的文本
        previousRawTextRef.current = inputText;
        previousProcessedTextRef.current = processedText;
    }, []);
    useEffect(() => {
        processText(text || '');
    }, [text, processText]);
    return markdownText;
}
export const AiDrawerHooks = Object.freeze({
    useLikeDislike,
    useIssueAnalysisQuoteHandler,
    useConvertAnswerToMarkdownText,
});
